import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

/**
 * `Timeline` — a presentational, vertical list of ordered steps. Each
 * `Timeline.Item` renders a status marker joined to the next item by a vertical
 * connector line, alongside a title, optional datetime subtext, and optional
 * detail children.
 *
 * Domain-agnostic: it knows nothing about orders or couriers. All labels come
 * in via props/children.
 *
 * ```tsx
 * <Timeline>
 *   <Timeline.Item status="completed" title="Курьер приехал" time="14:32 · 12.03" />
 *   <Timeline.Item status="current" title="Забрал заказ" time="14:40 · 12.03" />
 *   <Timeline.Item status="pending" title="Отдал клиенту" />
 * </Timeline>
 * ```
 */
function Timeline({ className, ...props }: React.ComponentProps<'ol'>) {
  return (
    <ol data-slot="timeline" className={cn('flex flex-col', className)} {...props} />
  );
}

const timelineMarkerVariants = cva('relative z-10 size-3 shrink-0 rounded-full', {
  variants: {
    status: {
      completed: 'bg-status-success',
      current: 'bg-status-active ring-4 ring-status-active/20',
      pending: 'border border-border bg-muted',
    },
  },
  defaultVariants: {
    status: 'pending',
  },
});

type TimelineItemStatus = NonNullable<
  VariantProps<typeof timelineMarkerVariants>['status']
>;

interface TimelineItemProps
  extends
    Omit<React.ComponentProps<'li'>, 'title'>,
    VariantProps<typeof timelineMarkerVariants> {
  status: TimelineItemStatus;
  title: React.ReactNode;
  time?: React.ReactNode;
  children?: React.ReactNode;
}

function TimelineItem({
  className,
  status,
  title,
  time,
  children,
  ...props
}: TimelineItemProps) {
  return (
    <li
      data-slot="timeline-item"
      data-status={status}
      className={cn(
        'flex gap-3 [&:last-child_[data-slot=timeline-connector]]:hidden',
        className,
      )}
      {...props}
    >
      <div className="flex flex-col items-center pt-1">
        <span
          data-slot="timeline-marker"
          className={timelineMarkerVariants({ status })}
          aria-hidden="true"
        />
        <span
          data-slot="timeline-connector"
          className="w-px grow bg-border"
          aria-hidden="true"
        />
      </div>
      <div className="flex flex-col gap-0.5 pb-6">
        <span className="text-sm font-medium text-foreground">{title}</span>
        {time !== undefined ? (
          <span className="text-xs text-muted-foreground">{time}</span>
        ) : null}
        {children}
      </div>
    </li>
  );
}

/**
 * Compound export: `Timeline` is the `<ol>` container; `Timeline.Item` is one
 * step. Assembled with `Object.assign` so callers write `<Timeline.Item />`.
 */
const TimelineNamespace = Object.assign(Timeline, { Item: TimelineItem });

export { TimelineNamespace as Timeline, timelineMarkerVariants };
export type { TimelineItemProps, TimelineItemStatus };
